import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface AsyncSearchableSelectOption {
  value: string;
  label: string;
}

interface UseAsyncSearchableSelectParams {
  id: string;
  initialOptions: AsyncSearchableSelectOption[];
  onSearch: (query: string) => Promise<AsyncSearchableSelectOption[]>;
  onOptionChange: (data: { name: string; value: string }) => void;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  error?: string;
  minSearchLength?: number;
  debounceMs?: number;
  /** When true, Enter commits the search text as the selected value without picking a list option. */
  isTypeable?: boolean;
  /**
   * When true, after a search completes with zero matches the trimmed query appears as a
   * selectable option, and Enter still commits that text as the value.
   */
  commitSearchOnEnterWhenNoResults?: boolean;
}

/**
 * Drives async search, keyboard handling, and selection state for {@link AsyncSearchableSelect}.
 *
 * @param params - Hook configuration
 * @returns Refs, option list state, and event handlers for the select UI
 */
export function useAsyncSearchableSelect({
  id,
  initialOptions,
  onSearch,
  onOptionChange,
  value,
  defaultValue,
  disabled = false,
  error: errorProp,
  minSearchLength = 2,
  debounceMs = 300,
  isTypeable = false,
  commitSearchOnEnterWhenNoResults = false,
}: UseAsyncSearchableSelectParams) {
  const [selected, setSelected] = useState<AsyncSearchableSelectOption>({
    value: "",
    label: "",
  });
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [asyncOptions, setAsyncOptions] = useState<
    AsyncSearchableSelectOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">(
    "bottom",
  );
  const [errorText, setErrorText] = useState(errorProp ?? "");

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setErrorText(errorProp ?? "");
  }, [errorProp]);

  useEffect(() => {
    const resolve = (v: string | undefined) => {
      if (!v) {
        setSelected({ value: "", label: "" });
        return;
      }
      const match = initialOptions.find((o) => o.value === v);
      setSelected(match ?? { value: v, label: v });
    };
    if (value) resolve(value);
    else if (defaultValue) resolve(defaultValue);
  }, [value, defaultValue, initialOptions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
        setAsyncOptions([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const displayedOptions = useMemo(() => {
    const trimmed = searchQuery.trim();

    if (trimmed.length >= minSearchLength && asyncOptions.length > 0) {
      return asyncOptions;
    }

    if (trimmed.length > 0 && trimmed.length < minSearchLength) {
      const q = trimmed.toLowerCase();
      return initialOptions.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q),
      );
    }

    if (
      trimmed.length >= minSearchLength &&
      asyncOptions.length === 0 &&
      !isLoading
    ) {
      if (commitSearchOnEnterWhenNoResults) {
        return [{ value: trimmed, label: trimmed }];
      }
      return [];
    }

    return initialOptions;
  }, [
    searchQuery,
    initialOptions,
    asyncOptions,
    minSearchLength,
    isLoading,
    commitSearchOnEnterWhenNoResults,
  ]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
      setSearchQuery("");
      setAsyncOptions([]);
    } else if (triggerRef.current) {
      const bottom = triggerRef.current.getBoundingClientRect().bottom || 0;
      const vh = window.innerHeight;
      setDropdownPosition(vh - bottom < vh * 0.45 ? "top" : "bottom");
      setIsOpen(true);
    }
  }, [disabled, isOpen]);

  const handleSelect = useCallback(
    (option: AsyncSearchableSelectOption) => {
      setSelected(option);
      setIsOpen(false);
      setSearchQuery("");
      setAsyncOptions([]);
      onOptionChange({ name: id, value: option.value });
    },
    [id, onOptionChange],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const q = e.target.value;
      setSearchQuery(q);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      const trimmed = q.trim();
      if (trimmed.length < minSearchLength) {
        setAsyncOptions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      debounceRef.current = setTimeout(async () => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        try {
          const results = await onSearch(trimmed);
          setAsyncOptions(results);
        } catch {
          setAsyncOptions([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    },
    [onSearch, minSearchLength, debounceMs],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearchQuery("");
        setAsyncOptions([]);
        return;
      }
      if (e.key !== "Enter") return;

      const trimmed = searchQuery.trim();

      if (isTypeable) {
        e.preventDefault();
        if (trimmed === "") return;
        handleSelect({ value: trimmed, label: trimmed });
        return;
      }

      if (commitSearchOnEnterWhenNoResults) {
        if (trimmed.length < minSearchLength || isLoading) return;
        if (asyncOptions.length > 0) return;
        e.preventDefault();
        handleSelect({ value: trimmed, label: trimmed });
      }
    },
    [
      isTypeable,
      commitSearchOnEnterWhenNoResults,
      searchQuery,
      minSearchLength,
      isLoading,
      asyncOptions.length,
      handleSelect,
    ],
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setAsyncOptions([]);
    setIsLoading(false);
    searchInputRef.current?.focus();
  }, []);

  return {
    selected,
    isOpen,
    searchQuery,
    isLoading,
    errorText,
    dropdownPosition,
    displayedOptions,
    triggerRef,
    dropdownRef,
    searchInputRef,
    handleToggle,
    handleSelect,
    handleSearchChange,
    handleKeyDown,
    handleClearSearch,
  };
}
